import {
  MenuItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { CommitmentTabProps } from "features/commitments/view/tabs/CommitmentTabProps";
import { useCommitmentInformationRetrieveSuspense } from "gen/endpoints/commitment-information/commitment-information";
import { useInvestorListSuspense } from "gen/endpoints/investor/investor";
import { useInvestorClassList } from "gen/endpoints/investor-class/investor-class";
import {
  getSecurityOwnershipCancelCorrectListQueryKey,
  useSecurityOwnershipCancelCorrect,
  useSecurityOwnershipCancelCorrectListSuspense,
} from "gen/endpoints/security-ownership/security-ownership";
import { CommitmentInformationData } from "gen/model/commitmentInformationData";
import { FundFamilyEventData } from "gen/model/fundFamilyEventData";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { InvestorReadData } from "gen/model/investorReadData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { SecurityOwnershipCancelCorrectCreateDataRequest } from "gen/model/securityOwnershipCancelCorrectCreateDataRequest";
import { securityOwnershipCancelCorrectBody } from "gen/schemas/security-ownership/security-ownership";
import { last } from "lodash-es";
import React, { useContext, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { UNSAFE_RouteContext, useNavigate, useParams } from "react-router-dom";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { Restricted } from "shared/components/access/Restricted";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import { PageTabs } from "shared/components/layout/PageTabs";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { useCurrentPageTabId } from "shared/navigation/useCurrentPageTabId";
import { formatIsoDateToDisplay, nowAsIsoDate } from "shared/utils/DateUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const CommitmentPage: React.FC = () => {
  const { t } = useTranslation(["commitment", "common"]);
  const { matches } = useContext(UNSAFE_RouteContext);
  const match = last(matches);
  const { idPrefix } = match?.route.handle ?? {};
  const fullHeightTabIds = [
    `${idPrefix}CommitmentActivity`,
    `${idPrefix}commitmentFees`,
  ];
  const {
    value: isCancelCorrectModalOpen,
    setTrue: openCancelCorrectModal,
    setFalse: closeCancelCorrectModal,
  } = useBoolean();
  const navigate = useNavigate();

  const currentPageTabId = useCurrentPageTabId();
  const { fundFamilyId, commitmentId } = useRequiredStringParams();
  const { investorEntityId } = useParams();

  const { data: commitment } = useCommitmentInformationRetrieveSuspense(
    fundFamilyId,
    commitmentId,
  );

  const { data: investors } = useInvestorListSuspense({
    investor_entity_ids: [commitment.owning_entity_id],
  });

  const { data: cancelCorrectData } =
    useSecurityOwnershipCancelCorrectListSuspense(fundFamilyId, commitmentId);
  const draftCancelCorrectEvent = cancelCorrectData.find(
    (event) =>
      event.event_state === FundFamilyEventStateMachineFieldsEnum.draft ||
      event.event_state === FundFamilyEventStateMachineFieldsEnum.out_of_date,
  );

  const tabProps: CommitmentTabProps = {
    fundFamilyId,
    commitmentId,
    investorClassId: commitment.investor_class_id,
    investorEntityId: commitment.owning_entity_id,
    fundId: commitment.owned_entity_id,
  };

  return (
    <PageContent
      contentWidth="wide"
      isFullHeight={fullHeightTabIds.includes(currentPageTabId)}
    >
      <PageHeader
        title={t("commitment:tabs.title", {
          date: commitment.closing_date
            ? formatIsoDateToDisplay(commitment.closing_date)
            : "",
        })}
        backButton={{
          title: t("common:commitments"),
          path: investorEntityId
            ? rootRoute({})
                .fundFamilies({})
                .fundFamily({ fundFamilyId })
                .commitments({})
                .investorEntities({})
                .investorEntity({ investorEntityId })
                .tabCommitments({}).$
            : rootRoute({})
                .fundFamilies({})
                .fundFamily({ fundFamilyId })
                .commitments({}).$,
        }}
        badge={
          commitment.owned_entity_legal_name
            ? {
                text: commitment.owned_entity_legal_name,
                color: "default",
              }
            : undefined
        }
        metadata={[
          {
            title: t("common:investors"),
            flattenIfSingle: true,
            children: investors.map((investor: InvestorReadData) => ({
              title: investor.name,
              to: rootRoute({})
                .investors({})
                .investor({ investorId: investor.id })
                .activity({}).$,
            })),
          },
          {
            title: commitment.owning_entity_legal_name,
            to: rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .setup({})
              .investorEntities({})
              .investorEntity({
                investorEntityId: commitment.owning_entity_id,
              })
              .tabActivity({}).$,
          },
          {
            title: t("common:commitment"),
          },
        ]}
        actions={
          <Restricted permission={PermissionTypeEnum.service}>
            <PageMenu>
              {draftCancelCorrectEvent ? (
                <MenuItem
                  onClick={() =>
                    navigate(
                      rootRoute({})
                        .fundFamilies({})
                        .fundFamily({ fundFamilyId })
                        .commitments({})
                        .cancelCorrect({
                          commitmentId,
                          fundFamilyEventId: draftCancelCorrectEvent.event_id,
                        }).$,
                    )
                  }
                >
                  {t("commitment:cancelCorrect.continueDraft")}
                </MenuItem>
              ) : (
                <MenuItem onClick={openCancelCorrectModal}>
                  {t("commitment:cancelCorrect.title")}
                </MenuItem>
              )}
            </PageMenu>
          </Restricted>
        }
      />
      <PageBody>
        <PageTabs context={tabProps} />
      </PageBody>
      {isCancelCorrectModalOpen && (
        <QueryBoundary>
          <CancelCorrectModal
            commitment={commitment}
            onCloseComplete={closeCancelCorrectModal}
          />
        </QueryBoundary>
      )}
    </PageContent>
  );
};

const CancelCorrectModal: React.FC<{
  onCloseComplete?: () => void;
  commitment: CommitmentInformationData;
}> = ({ onCloseComplete, commitment }) => {
  const { t } = useTranslation(["commitment", "common", "enums"]);
  const { fundFamilyId, commitmentId } = useRequiredStringParams();
  const { value: isOpen, setFalse: close } = useBoolean(true);
  const navigate = useNavigate();
  const cancelRef = useRef(null);
  const queryClient = useQueryClient();
  const { mutateAsync: cancelCorrectSecurityOwnership } =
    useSecurityOwnershipCancelCorrect();

  const { data: investorClassData } = useInvestorClassList(fundFamilyId);

  const defaultValues = {
    cancel_correct_investor_class_id: commitment.investor_class_id,
    effective_date: nowAsIsoDate(),
  } as SecurityOwnershipCancelCorrectCreateDataRequest;
  const form = useForm<SecurityOwnershipCancelCorrectCreateDataRequest>({
    defaultValues,
    resolver: zodResolver(securityOwnershipCancelCorrectBody),
  });
  const onSubmit = async (
    data: SecurityOwnershipCancelCorrectCreateDataRequest,
  ) =>
    await cancelCorrectSecurityOwnership({
      fundFamilyId,
      securityOwnershipId: commitmentId,
      data,
    });

  const onSuccess = async (event: FundFamilyEventData) => {
    await invalidateQueryKeys(
      queryClient,
      getSecurityOwnershipCancelCorrectListQueryKey(fundFamilyId, commitmentId),
    );
    close();
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .commitments({})
        .cancelCorrect({ commitmentId, fundFamilyEventId: event.id! }).$,
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      initialFocusRef={cancelRef}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("commitment:cancelCorrect.title")}</ModalHeader>
        <Text
          textStyle="small"
          color="black"
          marginBottom={2}
          ml={6}
          paddingRight="24px"
        >
          {t("commitment:cancelCorrect.editClass.description", {
            investor_entity_name: commitment.owning_entity_legal_name,
            amount: formatMoney(commitment.commitment_amount),
            class_name: commitment.investor_class_name,
          })}
        </Text>
        <Form form={form} onSubmit={onSubmit} onSuccess={onSuccess}>
          <ModalBody>
            <FormSelect
              name="cancel_correct_investor_class_id"
              title={t("commitment:cancelCorrect.editClass.newClass")}
            >
              {investorClassData
                ?.filter(
                  (investorClass) =>
                    investorClass.id != commitment.investor_class_id,
                )
                .map((investorClass) => (
                  <SelectOption key={investorClass.id} value={investorClass.id}>
                    {investorClass.name}
                  </SelectOption>
                ))}
            </FormSelect>
            <FormDateInput
              name="effective_date"
              title={t("commitment:cancelCorrect.editClass.customDate")}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={close} ref={cancelRef} />}
              confirm={
                <FormSubmitButton>
                  {t("commitment:cancelCorrect.title")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
